import React, { useState, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { useChromia } from '../context/ChromiaContext';
import { useAccount } from 'wagmi';
import { KEY_ENTER } from '../utils/constants';
import WalletConnect from './WalletConnect';

const StartMenu = () => {
    const [playerName, setPlayerName] = useState('');
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    
    const { gameStart, settings, updateSettings } = useGame();
    const { isConnected } = useAccount();
    const { 
        ftAccount, 
        hasAccount,
        isLoadingAccount, 
        chrBalance, 
        entryFee,
        canPayEntry,
        payEntryFee,
        isTransferring,
        isInitialized,
        refreshBalance,
        error: chromiaError
    } = useChromia();
    
    const audioRef = useRef({ split: null, spawn: null });

    const validNick = (name) => {
        const regex = /^\w*$/;
        return regex.test(name);
    };

    // Check if player can start the game
    const canPlay = isConnected && hasAccount && playerName.trim().length > 0 && validNick(playerName);

    const handlePayAndPlay = async () => {
        // Validate nickname first
        if (!validNick(playerName) || playerName.trim().length === 0) {
            setShowError(true);
            setErrorMessage(playerName.trim().length === 0 
                ? 'Please enter a nickname!' 
                : 'Nick must be alphanumeric characters only!');
            return;
        }

        if (!canPayEntry) {
            setShowError(true);
            setErrorMessage(`Insufficient CHR balance. Need ${entryFee} CHR.`);
            return;
        }

        setIsPaying(true);
        setShowError(false);

        try {
            // Pay entry fee
            await payEntryFee();
            
            // Play spawn sound
            const spawnAudio = document.getElementById('spawn_cell');
            if (spawnAudio) spawnAudio.play();
            
            // Start game
            const event = new CustomEvent('startGame', { 
                detail: { 
                    playerName: playerName.replace(/(<([^>]+)>)/ig, '').substring(0, 25), 
                    type: 'player',
                    ftAccountId: ftAccount?.id?.toString()
                } 
            });
            window.dispatchEvent(event);
        } catch (err) {
            setShowError(true);
            setErrorMessage(err.message || 'Payment failed. Please try again.');
        } finally {
            setIsPaying(false);
        }
    };

    const handleSpectate = () => {
        const event = new CustomEvent('startGame', { 
            detail: { 
                playerName: 'Spectator', 
                type: 'spectator'
            } 
        });
        window.dispatchEvent(event);
    };

    const handleKeyPress = (e) => {
        if (e.keyCode === KEY_ENTER && canPlay && canPayEntry) {
            handlePayAndPlay();
        }
    };

    const toggleSetting = (setting) => {
        updateSettings(setting, !settings[setting]);
    };

    if (gameStart) {
        return null;
    }

    // Determine the current step for the user flow
    const getStepStatus = () => {
        if (!isConnected) return 'connect-wallet';
        if (!isInitialized || isLoadingAccount) return 'loading';
        if (!hasAccount) return 'no-account';
        if (chrBalance < entryFee) return 'insufficient-balance';
        if (!playerName.trim()) return 'enter-name';
        return 'ready';
    };

    const stepStatus = getStepStatus();

    return (
        <div id="startMenuWrapper">
            <div id="startMenu">
                <p>Open Agar</p>

                {/* Step 1: Wallet Connection */}
                <div className="onboarding-section">
                    <div className={`step-indicator ${isConnected ? 'complete' : 'active'}`}>
                        <span className="step-number">1</span>
                        <span className="step-label">Connect Wallet</span>
                        {isConnected && <span className="step-check">✓</span>}
                    </div>
                    
                    {!isConnected && (
                        <div className="step-content">
                            <p className="step-description">Connect your wallet to start playing</p>
                            <div className="wallet-connect-inline">
                                <WalletConnect inline />
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 2: CHR Balance */}
                {isConnected && (
                    <div className="onboarding-section">
                        <div className={`step-indicator ${hasAccount && chrBalance >= entryFee ? 'complete' : 'active'}`}>
                            <span className="step-number">2</span>
                            <span className="step-label">CHR Balance</span>
                            {hasAccount && chrBalance >= entryFee && <span className="step-check">✓</span>}
                        </div>
                        
                        <div className="step-content">
                            {isLoadingAccount || !isInitialized ? (
                                <div className="balance-loading">
                                    <div className="spinner-small"></div>
                                    <span>Loading balance...</span>
                                </div>
                            ) : !hasAccount ? (
                                <div className="balance-warning">
                                    <p>⚠️ No FT4 account found on economy chain.</p>
                                    <p className="hint">Make sure you have a Chromia account with CHR tokens.</p>
                                </div>
                            ) : (
                                <div className="balance-display">
                                    <div className="balance-row">
                                        <span className="balance-label">Your Balance:</span>
                                        <span className={`balance-value ${chrBalance >= entryFee ? 'sufficient' : 'insufficient'}`}>
                                            {chrBalance.toFixed(2)} CHR
                                        </span>
                                        <button className="refresh-btn" onClick={refreshBalance} title="Refresh balance">
                                            ↻
                                        </button>
                                    </div>
                                    <div className="balance-row">
                                        <span className="balance-label">Entry Fee:</span>
                                        <span className="balance-value fee">{entryFee} CHR</span>
                                    </div>
                                    {chrBalance < entryFee && (
                                        <p className="balance-warning">
                                            ⚠️ You need {(entryFee - chrBalance).toFixed(2)} more CHR to play
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 3: Enter Nickname */}
                {hasAccount && (
                    <div className="onboarding-section">
                        <div className={`step-indicator ${playerName.trim() && validNick(playerName) ? 'complete' : 'active'}`}>
                            <span className="step-number">3</span>
                            <span className="step-label">Choose Nickname</span>
                            {playerName.trim() && validNick(playerName) && <span className="step-check">✓</span>}
                        </div>
                        
                        <div className="step-content">
                            <input
                                type="text"
                                id="playerNameInput"
                                placeholder="Enter your nickname"
                                maxLength="25"
                                value={playerName}
                                onChange={(e) => {
                                    setPlayerName(e.target.value);
                                    setShowError(false);
                                }}
                                onKeyDown={handleKeyPress}
                                autoFocus={hasAccount}
                            />
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {showError && (
                    <div className="error-message">
                        {errorMessage}
                    </div>
                )}

                {/* Play Button */}
                {hasAccount && (
                    <>
                        <button 
                            id="startButton" 
                            onClick={handlePayAndPlay}
                            disabled={!canPlay || !canPayEntry || isPaying || isTransferring}
                            className={(!canPlay || !canPayEntry || isPaying) ? 'disabled' : ''}
                        >
                            {isPaying || isTransferring ? (
                                <>
                                    <span className="spinner-btn"></span>
                                    Paying {entryFee} CHR...
                                </>
                            ) : (
                                `Pay ${entryFee} CHR & Play`
                            )}
                        </button>
                    </>
                )}

                <button id="spectateButton" onClick={handleSpectate}>
                    Spectate (Free)
                </button>

                <button id="settingsButton" onClick={() => setSettingsOpen(!settingsOpen)}>
                    Settings
                </button>
                <br />
                <div id="settings" className={settingsOpen ? 'open' : ''}>
                    <h3>Settings</h3>
                    <ul>
                        <label>
                            <input
                                id="visBord"
                                type="checkbox"
                                checked={settings.borderDraw}
                                onChange={() => toggleSetting('borderDraw')}
                            />
                            Show border
                        </label>
                        <label>
                            <input
                                id="showMass"
                                type="checkbox"
                                checked={settings.toggleMassState === 1}
                                onChange={() => updateSettings('toggleMassState', settings.toggleMassState === 0 ? 1 : 0)}
                            />
                            Show mass
                        </label>
                        <br />
                        <label>
                            <input
                                id="continuity"
                                type="checkbox"
                                checked={settings.continuity}
                                onChange={() => toggleSetting('continuity')}
                            />
                            Continue moving when mouse is off-screen
                        </label>
                        <br />
                        <label>
                            <input
                                id="roundFood"
                                type="checkbox"
                                checked={settings.roundFood}
                                onChange={() => toggleSetting('roundFood')}
                            />
                            Rounded food
                        </label>
                        <label>
                            <input
                                id="darkMode"
                                type="checkbox"
                                checked={settings.backgroundColor === '#181818'}
                                onChange={() => {
                                    if (settings.backgroundColor === '#f2fbff') {
                                        updateSettings('backgroundColor', '#181818');
                                        updateSettings('lineColor', '#ffffff');
                                    } else {
                                        updateSettings('backgroundColor', '#f2fbff');
                                        updateSettings('lineColor', '#000000');
                                    }
                                }}
                            />
                            Toggle Dark Mode
                        </label>
                    </ul>
                </div>
                <div id="instructions">
                    <h3>How to Play</h3>
                    <ul>
                        <li>💰 Pay {entryFee} CHR entry fee to start playing</li>
                        <li>🎮 Move mouse to control your cell, eat food to grow</li>
                        <li>⚔️ Eat smaller players, avoid bigger ones</li>
                        <li>💸 Press "Cash Out" to withdraw your winnings</li>
                        <li>⏱️ You'll freeze for 10 seconds - survive to get your CHR!</li>
                    </ul>
                </div>
                <audio id="split_cell" src="/audio/split.mp3" ref={el => audioRef.current.split = el}></audio>
                <audio id="spawn_cell" src="/audio/spawn.mp3" ref={el => audioRef.current.spawn = el}></audio>
            </div>
        </div>
    );
};

export default StartMenu;
